import { useNavigate } from 'react-router';
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    BadgeTone, Button, CardActions, CardGrid, ChipBar, ChipOption, MeetingCard, Section, SectionHeader, TabShell, TimeBadge,
} from 'components/ds';
import { Meeting } from 'components/home/meetingTypes';
import { useMeetingList } from 'hooks/useMeeting';
import { meetingStore } from 'store/meetingStore';
import { MeetingRepository } from 'repository/meetingRepository';
import { usePullToRefresh } from 'hooks/usePullToRefresh';
import styles from './Functions.module.css';
import { getMeetingSchedule as getSchedule } from 'common/utils/format';
import { fetchPlacePhotos, PlacePhoto } from 'common/utils/placePhotos';
import { CreateMeetingSection } from 'components/meeting/CreateMeetingSection';

const PAST_PAGE_SIZE = 10;

/**
 * 주요기능 탭.
 *
 * 담는 것 (기존 화면과 동일)
 *   모임 만들기(당일/여행) · 초대코드 · 액션 필요 · 나의 모임(오늘/예정/정기/완료)
 *   · 일정 조율 · 지난 모임 · 정산
 *
 * 형태는 홈과 같은 규칙입니다 — 섹션 제목 20px, 카드는 헤어라인 줄,
 * 왼쪽 선은 전부 거터(20px)에 맞춥니다.
 */
export const Functions = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { data, status } = useMeetingList();
    const isLoading = status === 'pending' || status === 'default';

    useEffect(() => {
        meetingStore.onGetMeetingList();
    }, []);


    const meetings: Meeting[] = data?.data?.map((meeting: any) => {
        const schedule = getSchedule(meeting);
        const place = meeting.place;
        return {
            id: meeting.meetingId,
            title: meeting.meetingName,
            host: meeting.creatorNickname ? decodeURIComponent(meeting.creatorNickname) : '',
            date: schedule ? new Date(schedule.startDatetime).toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', weekday: 'short' }) : '',
            time: schedule ? `${new Date(schedule.startDatetime).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}` : '',
            location: place?.placeAddress || place?.placeName || '',
            /* 카드 썸네일용 — 사진이 있으면 사진, 없으면 이름으로 자리를 채웁니다 */
            placeName: place?.placeName || '',
            externalPlaceId: place?.externalPlaceId || null,
            isLocationUndecided: !place?.placeName && !place?.placeAddress,
            isScheduleUndecided: !schedule,
            hasUnsettled: meeting.hasUnsettledPayment || false,
            paymentSettledAt: meeting.paymentSettledAt,
            isRegularMeeting: meeting.isRegularMeeting || false,
            memberCount: meeting.currentMemberCount || 1,
            status: (() => {
                // 종료 시각이 없으면 끝났는지 알 수 없어 '예정' 으로 둡니다.
                // (예전에는 new Date(undefined) 가 Invalid Date 가 되어 비교가
                //  늘 false → 같은 결과였는데, 의도가 안 보였습니다)
                if (!schedule?.endDatetime) return '예정';
                return new Date() > new Date(schedule.endDatetime) ? '종료' : '예정';
            })(),
            scheduleStartDateTime: schedule?.startDatetime,
            scheduleEndDateTime: schedule?.endDatetime,
        } as Meeting;
    }) || [];

    const needsAction = meetings.filter(m => m.isLocationUndecided || m.isScheduleUndecided || m.hasUnsettled);
    // 정산 섹션 — 종료 + 정산 미완료만. 정산 완료 처리된 모임은 종결로 보고 숨깁니다.
    const settlementMeetings = meetings.filter(m => m.status === '종료' && !(m as any).paymentSettledAt);
    const adjustableMeetings = meetings.filter(m => m.status === '예정');

    /* 나의 모임 — 오늘 / 예정 / 정기 / 완료 */
    type MeetingsTab = 'today' | 'upcoming' | 'regular' | 'completed';
    const [meetingsTab, setMeetingsTab] = useState<MeetingsTab>('today');

    const isToday = (dt?: string) => {
        if (!dt) return false;
        const d = new Date(dt);
        const now = new Date();
        return d.getFullYear() === now.getFullYear()
            && d.getMonth() === now.getMonth()
            && d.getDate() === now.getDate();
    };

    const tabLists = useMemo<Record<MeetingsTab, Meeting[]>>(() => ({
        today: meetings
            .filter(m => isToday(m.scheduleStartDateTime) && m.status === '예정')
            .sort((a, b) => new Date(a.scheduleStartDateTime!).getTime() - new Date(b.scheduleStartDateTime!).getTime()),
        upcoming: meetings
            .filter(m => m.status === '예정' && !isToday(m.scheduleStartDateTime)
                && !m.isRegularMeeting && m.scheduleStartDateTime)
            .sort((a, b) => new Date(a.scheduleStartDateTime!).getTime() - new Date(b.scheduleStartDateTime!).getTime()),
        regular: meetings.filter(m => m.isRegularMeeting === true),
        // 최근 종료 순
        completed: meetings
            .filter(m => m.status === '종료')
            .sort((a, b) => {
                const ae = (a as any).scheduleEndDateTime ? new Date((a as any).scheduleEndDateTime).getTime() : 0;
                const be = (b as any).scheduleEndDateTime ? new Date((b as any).scheduleEndDateTime).getTime() : 0;
                return be - ae;
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [data]);

    const tabOptions: ChipOption[] = (['today', 'upcoming', 'regular', 'completed'] as const).map(v => ({
        value: v,
        label: tabLists[v].length > 0
            ? `${t(`functions.tab${v[0].toUpperCase()}${v.slice(1)}`)} ${tabLists[v].length}`
            : t(`functions.tab${v[0].toUpperCase()}${v.slice(1)}`),
    }));

    /* 지난 모임 — 별도 API (ended=true) */
    const [pastMeetings, setPastMeetings] = useState<any[]>([]);

    /* 장소 사진 — 홈과 같은 방식(공용 fetchPlacePhotos). 사진이 없으면 카드가
       장소 이름으로 썸네일 자리를 채우므로 목록 정렬이 흐트러지지 않습니다. */
    const [placePhotoMap, setPlacePhotoMap] = useState<Record<string, PlacePhoto>>({});
    const photoFetchedRef = useRef<Set<string>>(new Set());
    const photoOf = (m: any): string | null => {
        const id = m?.externalPlaceId;
        return id ? (placePhotoMap[id]?.photoUrl || null) : null;
    };

    /* meetings 는 매 렌더 새 배열이므로 id 문자열로 고정해 불필요한 실행을 막습니다. */
    const photoKey = [...meetings, ...pastMeetings]
        .map((m: any) => m?.externalPlaceId as string | null)
        .filter((id): id is string => !!id)
        .sort()
        .join(',');

    useEffect(() => {
        const ids = Array.from(new Set(
            photoKey.split(',').filter(id => !!id && !photoFetchedRef.current.has(id))
        ));
        if (ids.length === 0) return;
        ids.forEach(id => photoFetchedRef.current.add(id));
        (async () => {
            try {
                const photos = await fetchPlacePhotos(ids);
                const next: Record<string, PlacePhoto> = {};
                Object.entries(photos).forEach(([id, v]) => { if (v?.photoUrl) next[id] = v; });
                if (Object.keys(next).length > 0) setPlacePhotoMap(prev => ({ ...prev, ...next }));
            } catch { /* 사진은 없어도 카드가 성립합니다 */ }
        })();
    }, [photoKey]);

    const loadPastMeetings = useCallback(async () => {
        try {
            const res: any = await MeetingRepository.onGetMyMeetings({ ended: true, page: 1, pageSize: PAST_PAGE_SIZE });
            const list = res?.data?.data?.list || res?.data?.list || res?.data?.data || [];
            const now = new Date();
            const todayTs = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
            const MS_DAY = 86400000;
            setPastMeetings(list.map((m: any) => {
                const sched = m.schedule || m.confirmedSchedule
                    || { startDatetime: m.startDatetime, endDatetime: m.endDatetime };
                const place = m.place;
                const endDate = sched?.endDatetime ? new Date(sched.endDatetime) : null;
                let daysAgo: number | null = null;
                if (endDate && !isNaN(endDate.getTime())) {
                    const endTs = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate()).getTime();
                    daysAgo = Math.max(0, Math.round((todayTs - endTs) / MS_DAY));
                }
                return {
                    id: m.meetingId,
                    title: m.meetingName,
                    date: endDate ? endDate.toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', weekday: 'short' }) : '',
                    location: place?.placeAddress || place?.placeName || '',
                    placeName: place?.placeName || '',
                    externalPlaceId: place?.externalPlaceId || null,
                    memberCount: m.currentMemberCount ?? m.memberCount ?? 1,
                    daysAgo,
                    isRegularMeeting: m.isRegularMeeting || false,
                    raceEnabled: Boolean(m.raceEnabled),
                    raceFinalizedAt: m.raceFinalizedAt || undefined,
                };
            }));
        } catch (e) {
            console.error('지난 모임 조회 실패:', e);
            setPastMeetings([]);
        }
    }, []);

    useEffect(() => {
        loadPastMeetings();
    }, [loadPastMeetings]);

    const { containerRef: pullRef, pullDistance, isRefreshing } = usePullToRefresh({
        onRefresh: async () => {
            meetingStore.onGetMeetingList();
            loadPastMeetings();
            await new Promise(r => setTimeout(r, 800));
        },
    });

    const goDetail = (id: any) => navigate(`/meeting/detail/${id}`);

    /** 모임 성격 배지 */
    const badgesOf = (m: any): { label: string; tone: BadgeTone }[] => {
        const list: { label: string; tone: BadgeTone }[] = [];
        if (m.isRegularMeeting) list.push({ label: t('home.regularTag'), tone: 'neutral' });
        if (m.raceEnabled) list.push({ label: m.raceFinalizedAt ? t('home.raceDone') : t('home.raceTag'), tone: 'weak' });
        return list;
    };

    const whenOf = (m: any) => m.isScheduleUndecided
        ? t('home.scheduleNotSet')
        : [m.date, m.time].filter(Boolean).join(' · ');

    const whereOf = (m: any) => m.isLocationUndecided
        ? t('meeting.placeNotSet')
        : (m.location || t('meeting.placeNotSet'));

    const memberLine = (m: any) => t('home.members', { count: m.memberCount });

    const cardOf = (m: any, opts?: {
        when?: string;
        extraBadges?: { label: string; tone: BadgeTone }[];
        time?: React.ReactNode;
        onClick?: () => void;
    }) => (
        <MeetingCard
            key={m.id}
            name={m.title}
            when={opts?.when ?? whenOf(m)}
            where={whereOf(m)}
            time={opts?.time}
            imageUrl={photoOf(m)}
            placeName={(m as any).placeName || undefined}
            badges={[...badgesOf(m), ...(opts?.extraBadges || [])]}
            people={<span className={styles.cardMembers}>{memberLine(m)}</span>}
            onClick={opts?.onClick ?? (() => goDetail(m.id))}
        />
    );

    const daysAgoLabel = (n: number | null) => {
        if (n === null) return '';
        if (n === 0) return t('functions.daysAgoToday');
        if (n === 1) return t('functions.daysAgoYesterday');
        return t('functions.daysAgo', { count: n });
    };

    return (
        <TabShell
            title={t('nav.features')}
            scrollRef={pullRef}
            pullDistance={pullDistance}
            refreshing={isRefreshing}
        >
            {/* ══ 모임 만들기 ══ */}
            <CreateMeetingSection spacing="tight" />

            {/* ══ 액션이 필요해요 ══ */}
            {needsAction.length > 0 && (
                <Section divided>
                    <SectionHeader title={t('functions.sectionAction')} count={needsAction.length} />
                    <CardGrid>
                        {needsAction.slice(0, 3).map(m => (
                            <div key={m.id}>
                                {cardOf(m, {
                                    extraBadges: [
                                        ...(m.isScheduleUndecided ? [{ label: t('functions.scheduleUndecided'), tone: 'outline' as BadgeTone }] : []),
                                        ...(m.isLocationUndecided ? [{ label: t('functions.placeUndecided'), tone: 'outline' as BadgeTone }] : []),
                                        ...(m.hasUnsettled ? [{ label: t('functions.settlementNeeded'), tone: 'danger' as BadgeTone }] : []),
                                    ],
                                })}
                                <CardActions>
                                    {m.isScheduleUndecided && (
                                        <Button variant="secondary" size="sm" onClick={() => navigate(`/schedule/main/${m.id}`)}>
                                            {t('functions.scheduleAdjust')}
                                        </Button>
                                    )}
                                    {m.hasUnsettled && (
                                        <Button variant="secondary" size="sm" onClick={() => navigate(`/cost/${m.id}`)}>
                                            {t('home.settlementBtn')}
                                        </Button>
                                    )}
                                </CardActions>
                            </div>
                        ))}
                    </CardGrid>
                </Section>
            )}

            {/* ══ 나의 모임 ══ */}
            <Section divided>
                <SectionHeader
                    title={t('functions.sectionMyMeetings')}
                    count={meetings.length}
                    moreLabel={t('common.viewAll')}
                    onMore={() => navigate('/my-meetings')}
                />
                <div className={styles.tabs}>
                    <ChipBar
                        options={tabOptions}
                        value={meetingsTab}
                        onChange={(v) => setMeetingsTab(v as MeetingsTab)}
                        ariaLabel={t('functions.sectionMyMeetings')}
                    />
                </div>
                {isLoading ? (
                    <p className={styles.empty}>{t('functions.loading')}</p>
                ) : tabLists[meetingsTab].length === 0 ? (
                    <p className={styles.empty}>
                        {t(`functions.empty${meetingsTab[0].toUpperCase()}${meetingsTab.slice(1)}`)}
                    </p>
                ) : (
                    <CardGrid>
                        {tabLists[meetingsTab].slice(0, 5).map(m => cardOf(m, {
                            extraBadges: meetingsTab === 'completed'
                                ? [(m as any).paymentSettledAt
                                    ? { label: t('functions.settledDone'), tone: 'weak' as BadgeTone }
                                    : m.hasUnsettled
                                        ? { label: t('functions.settling'), tone: 'danger' as BadgeTone }
                                        : { label: t('functions.settled'), tone: 'weak' as BadgeTone }]
                                : [],
                        }))}
                    </CardGrid>
                )}
            </Section>

            {/* ══ 일정 조율 ══ */}
            <Section divided>
                <SectionHeader title={t('functions.sectionScheduleAdjust')} count={adjustableMeetings.length} />
                {adjustableMeetings.length === 0 ? (
                    <p className={styles.empty}>{t('functions.noScheduleToAdjust')}</p>
                ) : (
                    <CardGrid>
                        {adjustableMeetings.slice(0, 3).map(m => (
                            <div key={m.id}>
                                {cardOf(m, {
                                    extraBadges: m.isScheduleUndecided
                                        ? [{ label: t('functions.unconfirmed'), tone: 'outline' as BadgeTone }]
                                        : [],
                                    onClick: () => navigate(`/schedule/main/${m.id}`),
                                })}
                                <CardActions>
                                    <Button variant="secondary" size="sm" onClick={() => navigate(`/schedule/main/${m.id}`)}>
                                        {t('functions.adjustBtn')}
                                    </Button>
                                </CardActions>
                            </div>
                        ))}
                    </CardGrid>
                )}
            </Section>

            {/* ══ 지난 모임 ══ */}
            {pastMeetings.length > 0 && (
                <Section divided>
                    <SectionHeader
                        title={t('functions.sectionPast')}
                        count={pastMeetings.length}
                        moreLabel={t('common.viewAll')}
                        onMore={() => navigate('/meetings?filter=completed')}
                    />
                    <CardGrid>
                        {pastMeetings.map(m => (
                            <div key={m.id}>
                                <MeetingCard
                                    name={m.title}
                                    when={m.date}
                                    where={m.location || t('meeting.placeNotSet')}
                                    time={<TimeBadge size="sm" past>{daysAgoLabel(m.daysAgo)}</TimeBadge>}
                                    imageUrl={photoOf(m)}
                                    placeName={m.placeName || undefined}
                                    badges={badgesOf(m)}
                                    people={<span className={styles.cardMembers}>{memberLine(m)}</span>}
                                    onClick={() => goDetail(m.id)}
                                />
                                <CardActions>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => navigate(`/meeting/create?recreate=${m.id}`)}
                                    >
                                        {t('functions.recreate')}
                                    </Button>
                                </CardActions>
                            </div>
                        ))}
                    </CardGrid>
                </Section>
            )}

            {/* ══ 정산 ══ */}
            {settlementMeetings.length > 0 && (
                <Section divided last>
                    <SectionHeader
                        title={t('functions.sectionSettlement')}
                        count={settlementMeetings.length}
                        moreLabel={t('common.viewAll')}
                        onMore={() => navigate('/meetings?filter=completed')}
                    />
                    <CardGrid>
                        {settlementMeetings.slice(0, 3).map(m => (
                            <div key={m.id}>
                                {cardOf(m, {
                                    extraBadges: [m.hasUnsettled
                                        ? { label: t('functions.unsettled'), tone: 'danger' as BadgeTone }
                                        : { label: t('functions.settled'), tone: 'weak' as BadgeTone }],
                                    onClick: () => navigate(`/cost/${m.id}`),
                                })}
                                <CardActions>
                                    <Button variant="secondary" size="sm" onClick={() => navigate(`/cost/${m.id}`)}>
                                        {m.hasUnsettled ? t('functions.settleBtn') : t('functions.viewHistory')}
                                    </Button>
                                </CardActions>
                            </div>
                        ))}
                    </CardGrid>
                </Section>
            )}
        </TabShell>
    );
};
